/// <amd-module name="Wit/LinkedWorkItemsDropdown/LinkedWorkItemsService" />
import { getTFSPageData, IProjectData } from "Tfs/Platform/Page";
import { localeIgnoreCaseComparer } from "VSS/Core/Util/String";
import { IVssPageContext, IVssReadConsistencyLevel, IVssService, Services, VssService } from "VSS/Platform/Context";
import { IVssContributionService } from "VSS/Platform/Contribution";
import { isFeatureFlagEnabled } from "VSS/Platform/Feature";
import { Wiql, WorkItem, WorkItemExpand, WorkItemQueryResult, WorkItemType } from "Wit/Clients/Wit/Contracts/WorkItemTracking";
import { getWorkItemTrackingClient, IWorkItemTrackingRestClient } from "Wit/Clients/Wit/RestClient/WorkItemTracking";
import { CoreFieldRefNames, WorkItemStateCategories } from "Wit/Common/Generated/Constants";
import { JsonPatchDocument, JsonPatchOperation, Operation } from "Wit/LinkedWorkItemsDropdown/Contracts/WitBatchRestClient";
import * as Resources from "Wit/LinkedWorkItemsDropdown/Resources";
import { getWitBatchClient } from "Wit/LinkedWorkItemsDropdown/RestClient/WitBatchRestClient";
import { WorkItemMentionDataProvider } from "Wit/MentionsAutocomplete/Providers/WorkItemMentionDataProvider";
import { WorkItemTrackingDataProvider } from "Wit/UI/Data";
import { getGitClient, IGitRestClient } from "./RestClient/Git";
import { getChangesetArtifactLink, getCommitArtifactLink } from "./Util/Artifact";

export interface IArtifactLink {
    name: string;
    uri: string;
}

interface AssociatedWorkItemsRequestParameters {
    repositoryId: string;
    versions: string[];
}

interface AssociatedWorkItemsResult {
    id: number;
}

export interface ILinkedWorkItemsService extends IVssService {
    addWorkItemsBatchAsync(workItemIds: number[], artifactLinks: IArtifactLink[]): Promise<void>;
    /**
     * Gets work items assigned to the user
     */
    getAssignedWorkItems(projectId?: string): Promise<WorkItemQueryResult>;
    getDataProvider(): WorkItemTrackingDataProvider;
    getLinkedPullRequestWorkItemIds(repositoryId: string, pullRequestId: number): Promise<number[]>;
    getLinkedChangesetWorkItemIds(changesetId: number): Promise<number[]>;
    getLinkedCommitWorkItemIds(projectId: string, repositoryId: string, commitId: string): Promise<number[]>;
    getLinkedShelvesetWorkItemIds(shelvesetName: string): Promise<number[]>;
    getWorkItem(workItemId: number, project: string, expand?: WorkItemExpand): Promise<WorkItem>;
    getWorkItems(workItemIds: number[], project: string, expand?: WorkItemExpand): Promise<WorkItem[]>;
    removeWorkItemsBatchAsync(workItems: WorkItem[], artifactLinks: IArtifactLink[]): Promise<void>;
    searchWorkItemIds(searchTerm: string, idsToExclude: number[], projectId?: string, useSearchApi?: boolean): Promise<number[]>;
}

const workItemBatchSize = 200;
const titleSearchPageSize = 50; // limit the page size to ensure that the query completes fairly quickly

class LinkedWorkItemsService extends VssService implements ILinkedWorkItemsService {
    private dataProvider: WorkItemTrackingDataProvider;
    private gitClient: IGitRestClient;
    private witClient: IWorkItemTrackingRestClient;
    private witReadOnlyClient: IWorkItemTrackingRestClient;
    private project: IProjectData;
    private workItemTypesPromise: Promise<WorkItemType[]> | undefined;
    private mentionsDataProvider: WorkItemMentionDataProvider | undefined;

    public _serviceStart(pageContext: IVssPageContext) {
        super._serviceStart(pageContext);

        this.dataProvider = new WorkItemTrackingDataProvider(pageContext);
        this.gitClient = getGitClient(pageContext);
        this.witClient = getWorkItemTrackingClient(pageContext);
        if (isFeatureFlagEnabled(pageContext, "WebAccess.WorkItemTracking.UseReadReplicaForWebUI")) {
            this.witReadOnlyClient = getWorkItemTrackingClient(pageContext, {
                readConsistencyLevel: IVssReadConsistencyLevel.Eventual
            });
        } else {
            this.witReadOnlyClient = this.witClient;
        }

        this.project = getTFSPageData(this.pageContext)!.project!;
    }

    public async addWorkItemsBatchAsync(workItemIds: number[], artifactLinks: IArtifactLink[]) {
        const batchWitClient = getWitBatchClient(this.pageContext);

        const operations = workItemIds.map((workItemId: number): [number, JsonPatchDocument] => {
            return [workItemId, artifactLinks.map(al => this.getWorkItemPatchAddOperation(al))];
        });

        return batchWitClient.updateWorkItemsBatch(operations).then(responses => {
            const failedResponses = responses.filter(response => response.code >= 400);
            if (failedResponses.length) {
                const firstBody = JSON.parse(failedResponses[0].body);
                throw new Error(this.tryGetErrorMessage(firstBody) || Resources.BatchWorkItemsAddError);
            }
        });
    }

    public async getAssignedWorkItems(projectId?: string): Promise<WorkItemQueryResult> {
        const workItemTypes = await this.getWorkItemTypes(projectId);
        const wiql = this.generateWiqlForCurrentUsersItems(workItemTypes);

        return this.witReadOnlyClient.queryByWiql(wiql, this.project?.id ?? projectId);
    }

    public async searchWorkItemIds(searchTerm: string, idsToExclude: number[], projectId?: string, useSearchApi?: boolean): Promise<number[]> {
        if (useSearchApi) {
            const mentionsDataProvider = this.getMentionsDataProvider();
            if (mentionsDataProvider.isRestSearchForMentionEnabled()) {
                const searchResult = await mentionsDataProvider.searchOnServerAsync(searchTerm);
                return searchResult.map(item => item.original.id).filter(id => !idsToExclude.includes(id));
            }
        }

        const wiql = this.generateWiqlForTitleSearch(searchTerm, idsToExclude);
        const queryResult = await this.witReadOnlyClient.queryByWiql(wiql, this.project?.id ?? projectId, undefined, undefined, titleSearchPageSize);
        return queryResult.workItems.map(wi => wi.id);
    }

    public getDataProvider() {
        return this.dataProvider;
    }

    public async getLinkedPullRequestWorkItemIds(repositoryId: string, pullRequestId: number): Promise<number[]> {
        return this.gitClient.getPullRequestWorkItemRefs(repositoryId, pullRequestId, this.project.id).then(result => {
            return result.map(x => parseInt(x.id));
        });
    }

    public async getLinkedChangesetWorkItemIds(changesetId: number): Promise<number[]> {
        const artifactLinkData = getChangesetArtifactLink(changesetId);
        return this.getLinkedArtifactWorkItemIds(artifactLinkData);
    }

    public async getLinkedShelvesetWorkItemIds(shelvesetName: string): Promise<number[]> {
        // Shelvesets are not saved as artifact, instead WIT is stored inside the shelveset Links property.
        // This is why we use this data provider to get linked WITs.
        const contributionService = this.pageContext.getService<IVssContributionService>("IVssContributionService");
        return contributionService
            .getDataAsync<AssociatedWorkItemsResult[]>(
                "ms.vss-code-web.associated-workitems-data-provider",
                { repositoryId: "", versions: [`S${shelvesetName}`] } as AssociatedWorkItemsRequestParameters,
                true
            )
            .then(workItems => (workItems ? workItems.map(workItem => workItem.id) : []));
    }

    public async getLinkedCommitWorkItemIds(projectId: string, repositoryId: string, commitId: string): Promise<number[]> {
        const artifactLinkData = getCommitArtifactLink(projectId, repositoryId, commitId, "commit");
        return this.getLinkedArtifactWorkItemIds(artifactLinkData);
    }

    public async getWorkItem(workItemId: number, project: string, expand?: WorkItemExpand): Promise<WorkItem> {
        return this.witReadOnlyClient.getWorkItem(workItemId, project, undefined, undefined, expand);
    }

    public async getWorkItems(workItemIds: number[], project: string, expand?: WorkItemExpand): Promise<WorkItem[]> {
        const results: WorkItem[] = [];
        let fetchedCount = 0;
        while (fetchedCount < workItemIds.length) {
            results.push(
                ...(await this.witReadOnlyClient.getWorkItems(
                    workItemIds.slice(fetchedCount, fetchedCount + workItemBatchSize),
                    project,
                    undefined,
                    undefined,
                    expand
                ))
            );
            fetchedCount += workItemBatchSize;
        }
        return results;
    }

    public async removeWorkItemsBatchAsync(workItems: WorkItem[], artifactLinks: IArtifactLink[]) {
        const batchWitClient = getWitBatchClient(this.pageContext);

        const operations = workItems.map((workItem: WorkItem): [number, JsonPatchDocument] => {
            return [workItem.id, artifactLinks.map(al => this.getWorkItemPatchRemoveOperation(al, workItem))];
        });

        return batchWitClient.updateWorkItemsBatch(operations).then(responses => {
            const failedResponses = responses.filter(response => response.code >= 400);
            if (failedResponses.length) {
                const firstBody = JSON.parse(failedResponses[0].body);
                throw new Error(this.tryGetErrorMessage(firstBody) || Resources.BatchWorkItemsRemoveError);
            }
        });
    }

    private async getLinkedArtifactWorkItemIds(artifactLinkData: IArtifactLink): Promise<number[]> {
        const artifactUris: string[] = [artifactLinkData.uri];

        return this.witReadOnlyClient.queryWorkItemsForArtifactUris({ artifactUris: artifactUris }).then(result => {
            const artifactData = result.artifactUrisQueryResult[artifactLinkData.uri];
            if (artifactData) {
                return artifactData.map(x => x.id);
            } else {
                return [];
            }
        });
    }

    private getMentionsDataProvider(): WorkItemMentionDataProvider {
        if (!this.mentionsDataProvider) {
            this.mentionsDataProvider = new WorkItemMentionDataProvider(this.pageContext, { useRestSearch: true });
        }
        return this.mentionsDataProvider;
    }

    private async getWorkItemTypes(projectId?: string): Promise<WorkItemType[]> {
        if (!this.workItemTypesPromise) {
            this.workItemTypesPromise = this.witReadOnlyClient.getWorkItemTypes(this.project?.id ?? projectId);
        }

        return this.workItemTypesPromise;
    }

    private generateWiqlForCurrentUsersItems(workItemTypes: WorkItemType[]): Wiql {
        // Filter-out any "completed" work items
        const typeConditions: string[] = [];
        workItemTypes.forEach(workItemType => {
            const terminalStates = workItemType.states.filter(
                state => state.category === WorkItemStateCategories.Completed || state.category === WorkItemStateCategories.Removed
            );
            if (terminalStates.length) {
                typeConditions.push(
                    `AND NOT ([${CoreFieldRefNames.WorkItemType}] = '${workItemType.name}' AND [${CoreFieldRefNames.State}] IN (${terminalStates
                        .map(s => `'${s.name}'`)
                        .join(",")}))`
                );
            }
        });

        const query = `SELECT [${CoreFieldRefNames.Id}]
            FROM WorkItems 
            WHERE (([${CoreFieldRefNames.AuthorizedAs}] = @me AND [${CoreFieldRefNames.AuthorizedDate}] >= @today - 30)
                   OR ([${CoreFieldRefNames.CreatedBy}] = @me AND [${CoreFieldRefNames.CreatedDate}] >= @today - 30)
                   OR ([${CoreFieldRefNames.AssignedTo}] = @me AND [${CoreFieldRefNames.AuthorizedDate}] >= @today - 30)
                  ) ${typeConditions.length ? typeConditions.join("\n") : ""}
            ORDER BY [${CoreFieldRefNames.AuthorizedDate}] DESC`;

        return { query };
    }

    private generateWiqlForTitleSearch(searchTerm: string, idsToExclude: number[]): Wiql {
        const normalizedSearch = searchTerm.replace(/'/g, '"'); // Make sure we're not accidentally breaking out of quotes
        const query = `SELECT [${CoreFieldRefNames.Id}] 
            FROM WorkItems 
            WHERE [${CoreFieldRefNames.Title}] CONTAINS '${normalizedSearch}'
            ${idsToExclude && idsToExclude.length > 0 ? `AND [${CoreFieldRefNames.Id}] NOT IN (${idsToExclude.join(",")})` : ""}`;
        return { query };
    }

    private getWorkItemPatchAddOperation(artifactLink: IArtifactLink): JsonPatchOperation {
        return {
            op: Operation.Add,
            path: "/relations/-",
            value: {
                attributes: { name: artifactLink.name },
                rel: "ArtifactLink",
                url: artifactLink.uri
            }
        } as JsonPatchOperation;
    }

    private getWorkItemPatchRemoveOperation(artifactLink: IArtifactLink, workItem: WorkItem): JsonPatchOperation {
        const relationIndex =
            workItem.relations && workItem.relations.findIndex(relation => localeIgnoreCaseComparer(relation.url, artifactLink.uri) === 0);

        return {
            op: Operation.Remove,
            path: `/relations/${relationIndex}`
        } as JsonPatchOperation;
    }

    private tryGetErrorMessage(errorBody: any): string | undefined {
        return (errorBody && errorBody.value && errorBody.Message) || (errorBody && errorBody.errorMessage);
    }
}

const serviceName = "ILinkedWorkItemsService";
export function getLinkedWorkItemsService(pageContext: IVssPageContext) {
    return pageContext.getService<ILinkedWorkItemsService>(serviceName);
}

Services.add(serviceName, { serviceFactory: LinkedWorkItemsService });
